import { useCallback, useEffect, useRef, useState } from 'react'

const OPEN_DELAY_MS = 70
export const CLOSE_DELAY_MS = 200
export const CLICK_GRACE_MS = 1000

type PointerHandler = (event: React.PointerEvent<HTMLElement>) => void

export function useHoverMenu() {
  const [isOpen, setIsOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const isPointerDrivenRef = useRef(false)
  const hoverOpenedAtRef = useRef(0)

  const cancelPending = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
  }, [])

  useEffect(() => cancelPending, [cancelPending])

  const handlePointerEnter = useCallback<PointerHandler>(
    (event) => {
      if (event.pointerType !== 'mouse') return

      cancelPending()
      timerRef.current = setTimeout(() => {
        isPointerDrivenRef.current = true
        hoverOpenedAtRef.current = Date.now()
        setIsOpen(true)
      }, OPEN_DELAY_MS)
    },
    [cancelPending],
  )

  const handlePointerLeave = useCallback<PointerHandler>(
    (event) => {
      if (event.pointerType !== 'mouse') return

      cancelPending()
      timerRef.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS)
    },
    [cancelPending],
  )

  // Hover has usually opened the menu by the time the click lands, so a click that soon must not toggle it shut.
  const handlePointerDown = useCallback<PointerHandler>(
    (event) => {
      if (event.pointerType !== 'mouse') return

      event.preventDefault()
      cancelPending()
      isPointerDrivenRef.current = true
      const isJustHoverOpened = Date.now() - hoverOpenedAtRef.current < CLICK_GRACE_MS
      setIsOpen((wasOpen) => !wasOpen || isJustHoverOpened)
    },
    [cancelPending],
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      cancelPending()
      if (open) isPointerDrivenRef.current = false
      setIsOpen(open)
    },
    [cancelPending],
  )

  // Hovering must not steal focus from whatever the operator is typing in; keyboard opens still do.
  const handleAutoFocus = useCallback((event: Event) => {
    if (isPointerDrivenRef.current) event.preventDefault()
  }, [])

  // Radix counts the trigger as outside the content and would dismiss on every trigger click.
  const handlePointerDownOutside = useCallback((event: CustomEvent<{ originalEvent: PointerEvent }>) => {
    if (triggerRef.current?.contains(event.detail.originalEvent.target as Node)) event.preventDefault()
  }, [])

  return {
    rootProps: { open: isOpen, onOpenChange: handleOpenChange, modal: false as const },
    triggerProps: {
      ref: triggerRef,
      onPointerEnter: handlePointerEnter,
      onPointerLeave: handlePointerLeave,
      onPointerDown: handlePointerDown,
    },
    contentProps: {
      onPointerEnter: handlePointerEnter,
      onPointerLeave: handlePointerLeave,
      onOpenAutoFocus: handleAutoFocus,
      onCloseAutoFocus: handleAutoFocus,
      onPointerDownOutside: handlePointerDownOutside,
    },
  }
}
